// Light and dark.
//
// The palette lives in src/styles/app.css: the dark scheme is the `:root`
// block, and the light scheme redefines the same `--la-*` names under
// `:root[data-scheme='light']`. So choosing a scheme is writing ONE attribute
// on <html>, and this file is what writes it.
//
// Three choices, kept on this phone: System follows the OS and changes with it
// at sunset, Light and Dark pin one. System is the default because a phone set
// to light at noon should not open onto a black screen, and a phone set to dark
// at 6:40am should not open onto a white one.
//
// Alongside the attribute, the same decision goes to the three things outside
// the stylesheet that paint a colour: Ionic's own dark palette (the
// `ion-palette-dark` class, from palettes/dark.class.css), the browser chrome
// (`theme-color`), and on a phone the status bar, whose text must be dark on
// paper and light on the carriage.
//
// index.html runs a copy of `resolve` inline before the first paint, reading
// the same key, so a light-mode phone never flashes the dark board first. The
// key and the values are repeated there by necessity — that script runs before
// any module loads — and `tests/theme.test.js` holds the two to each other.

import { ref } from 'vue';
import { prefs } from './core/storage.js';
import { isNative, platform } from './core/native.js';

export const THEME_KEY = 'lateagain:theme:v1';
export const THEMES = ['system', 'light', 'dark'];

/** The ground each scheme paints, for chrome that cannot read a CSS variable. */
export const SCHEME_GROUND = { light: '#f5f3ef', dark: '#14161a' };

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The stored choice. Anything unrecognised is System. */
export const theme = ref(readTheme());

function readTheme() {
  const v = prefs[THEME_KEY];
  return THEMES.includes(v) ? v : 'system';
}

/** Which scheme a choice comes to, given whether the OS is dark right now. */
export function resolve(choice, osDark) {
  if (choice === 'light' || choice === 'dark') return choice;
  return osDark ? 'dark' : 'light';
}

function osIsDark() {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches;
}

/** The scheme on screen now. */
export const scheme = ref(resolve(theme.value, osIsDark()));

/** Set the choice and repaint. */
export function setTheme(choice) {
  theme.value = THEMES.includes(choice) ? choice : 'system';
  prefs[THEME_KEY] = theme.value;
  apply();
}

function apply() {
  const next = resolve(theme.value, osIsDark());
  scheme.value = next;
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.dataset.scheme = next;
  root.classList.toggle('ion-palette-dark', next === 'dark');

  // One theme-color, not the pair index.html ships with: those follow the OS,
  // and a pinned choice is exactly the case where the OS is wrong.
  document.querySelectorAll('meta[name="theme-color"]').forEach((m, i) => {
    if (i === 0) {
      m.removeAttribute('media');
      m.setAttribute('content', SCHEME_GROUND[next]);
    } else {
      m.remove();
    }
  });

  paintStatusBar(next);
}

async function paintStatusBar(next) {
  if (!isNative) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    // Style names the BACKGROUND it suits: Style.Dark is light text.
    await StatusBar.setStyle({ style: next === 'dark' ? Style.Dark : Style.Light });
    if (platform === 'android') await StatusBar.setBackgroundColor({ color: SCHEME_GROUND[next] });
  } catch {
    // No plugin in this build, or a platform that refuses. The bar keeps
    // whatever the native theme gave it, which is legible, just not matched.
  }
}

/** Paint the stored choice, and follow the OS while the choice is System. */
export function initTheme() {
  apply();
  if (typeof matchMedia !== 'function') return;
  const mq = matchMedia(DARK_QUERY);
  const onChange = () => { if (theme.value === 'system') apply(); };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);
}
